import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { EventDispatcher } from 'three';
import { PageContainer } from '../../modules/page-container/page-container';
import { PageHeaderArticleDetailsLayout } from '../../visual-components/page-header-article-details-layout/page-header-article-details-layout';
import { Link } from '../../visual-components/link/link';
import { usePathBuilder } from '../../core/routing/hooks/use-path-builder';
import { RouteParam } from '../../core/routing/param';
import { useArticleDetailQuery } from '../../modules/article/queries';
import { useMandatoryPathParam } from '../../core/routing/hooks/use-mandatory-path-param';
import { PageHeaderArticleInformation } from '../../visual-components/page-header-article-information/page-header-article-information';
import { FullPageLoader } from '../../visual-components/full-page-loader/full-page-loader';
import isNil, { notNil } from '../../core/helpers/is-nil';
import { NotFoundView } from '../../modules/not-found-view/not-found-view';
import { GridContainer, GridItem } from '../../visual-components/grid/grid';
import { Typography } from '../../visual-components/typography/typography';
import { Spacer } from '../../visual-components/spacer/spacer';
import { Accordion, AccordionItem } from '../../visual-components/accordion/accordion';
import { UL } from '../../visual-components/list/unordered/unordered';
import { ULI } from '../../visual-components/list/item/item';
import { useLastVisitedArticles } from '../../modules/last-visited-articles/hooks/use-last-visited-articles';
import { CartSection } from '../../modules/cart/components/cart-section';
import { Icon } from '../../visual-components/icon/icon';
import { ThreeDModel } from '../../modules/article-preview/three-d-model';
import { PageHeaderColorSample } from '../../visual-components/page-header-color-sample/page-header-color-sample';
import { useStableFunction } from '../../core/hooks/use-stable-function';
import { useTrackOnce } from '../../modules/analytics/use-track-once';
import ErrorBoundary from '../../modules/error-boundary/error-boundary';
import { useShippingAddressId } from '../../modules/customer/hooks/use-shipping-address-id';
import { ImageTransformType, ImageType } from '../../modules/image/constants';
import { Image } from '../../modules/image/image';
import { Downloads } from './components/downloads';
import { CuringConditionInfo } from './components/curing-condition-info';
import styles from './overview.module.scss';

export type ThreeDModelEvent = {
  close: { type: 'close' };
};

export const Overview: React.FC = () => {
  const { t } = useTranslation();
  const { homePath, orderSampleSheet, serviceRequest } = usePathBuilder();
  const { shippingAddressId } = useShippingAddressId();

  const [isFocused, setFocusedPrivate] = useState(false);
  const eventDispatcherRef = useRef(new EventDispatcher<ThreeDModelEvent>());
  const track3DCloseUpView = useTrackOnce({ type: '3d-close-up-view' });
  const track3DCloseUpExit = useTrackOnce({ type: '3d-close-up-exit' });
  const setFocused = useStableFunction((focused: boolean) => {
    setFocusedPrivate(focused);
    if (focused) {
      track3DCloseUpView();
    } else {
      eventDispatcherRef.current.dispatchEvent({ type: 'close' });
      track3DCloseUpExit();
    }
  });

  const articleId = useMandatoryPathParam(RouteParam.articleId);
  const { updateLastVisitedArticles } = useLastVisitedArticles();
  useEffect(
    function handleUpdateLastVisitedArticles() {
      updateLastVisitedArticles(articleId);
    },
    [articleId, updateLastVisitedArticles],
  );

  const { data: articleData, isLoading } = useArticleDetailQuery(articleId);

  const article = articleData?.data;

  if (isLoading) {
    return <FullPageLoader />;
  }

  if (isNil(article)) {
    return <NotFoundView />;
  }

  const { effect, bonding } = article;
  const effectBondingString = notNil(effect) && notNil(bonding) ? `${effect}, ${bonding}` : effect;
  const characteristics = [article.gloss, article.surface, article.weatherResistance, effectBondingString].filter(
    notNil,
  );

  const hasCharacteristics = characteristics.length > 0;

  const hasSpecialProperties = article.specialProperties.length > 0;

  const hasCuringInformation =
    notNil(article.curingNote) || notNil(article.imageCuringCondition) || article.curingCondition.length > 0;

  const hasAnyBaseInfo = hasCharacteristics || hasSpecialProperties || hasCuringInformation;
  const safetyLabel = article.safetyLabel;

  return (
    <PageContainer
      documentTitle={article.id}
      pageHeader={{
        type: 'custom',
        content: (
          <PageHeaderArticleDetailsLayout
            addToCartSection={({ state }) => <CartSection article={article} moveOut={isFocused} state={state} />}
            articleInformation={
              <PageHeaderArticleInformation
                itemDesc2={article.itemDesc2}
                itemDesc3={article.itemDesc3}
                moveOut={isFocused}
                pictoSrc={article.icon ?? undefined}
                productTitle={article.id}
                seriesName={article.series.name}
                seriesNumber={article.series.number}
                seriesNumberColor={article.series.numberColor ?? undefined}
              />
            }
            backLink={
              <Link icon="arrow-left" iconPosition="left" to={homePath()}>
                {t('search.backToSearch')}
              </Link>
            }
            colorSample={
              article.hasRendering ? (
                <ErrorBoundary fallback={() => <PageHeaderColorSample hexColor={article.hexCode ?? undefined} />}>
                  <ThreeDModel
                    modelId={articleId}
                    detail={{
                      eventHub: eventDispatcherRef.current,
                      isFocused,
                      setFocused,
                    }}
                  />
                </ErrorBoundary>
              ) : (
                <PageHeaderColorSample hexColor={article.hexCode ?? undefined} />
              )
            }
          />
        ),
      }}
    >
      <div className={styles.closeButtonContainer}>
        <button
          className={classNames(styles.closeButton, {
            [styles.active]: isFocused,
          })}
          onClick={() => {
            setFocused(false);
          }}
        >
          <Typography tag="span" variant="buttontext">
            {t('a11y.close')}
          </Typography>
          <Icon size="24" variant="cross" />
        </button>
      </div>
      <div
        className={classNames(styles.contentPusher, {
          [styles.moveOut]: isFocused,
        })}
      />
      <GridContainer>
        <GridItem span="8">
          <Typography variant="headline-2">{article.title}</Typography>
          {notNil(article.title) ? (
            <>
              <Spacer size="06" />
              <Typography variant="leadtext">{article.shortDescription}</Typography>
            </>
          ) : null}
          {notNil(article.description) ? (
            <>
              <Spacer size="06" />
              <Typography variant="maintext">{article.description}</Typography>
            </>
          ) : null}
          <Spacer size="07" />
          {hasAnyBaseInfo ? (
            <Accordion
              title={t('articleDetail.details.title')}
              items={[
                hasCharacteristics
                  ? ({
                      picto: 'picto-characteristics',
                      triggerText: t('articleDetail.details.characteristics'),
                      content: (
                        <UL>
                          {characteristics.map((characteristic, i) => (
                            <ULI key={`${characteristic}-${i}`}>{characteristic}</ULI>
                          ))}
                        </UL>
                      ),
                      value: 'characteristics',
                    } satisfies AccordionItem)
                  : null,
                hasSpecialProperties
                  ? ({
                      picto: 'picto-functional-features',
                      triggerText: t('articleDetail.details.functionalFeatures'),
                      content: (
                        <UL>
                          {article.specialProperties.map((property, i) => (
                            <ULI key={`${property}-${i}`}>{property}</ULI>
                          ))}
                        </UL>
                      ),
                      value: 'functional-features',
                    } satisfies AccordionItem)
                  : null,
                hasCuringInformation
                  ? ({
                      picto: 'picto-burn-in-conditions',
                      triggerText: t('articleDetail.details.curingConditions'),
                      content: (
                        <CuringConditionInfo
                          curingCondition={article.curingCondition}
                          curingNote={article.curingNote}
                          image={article.imageCuringCondition}
                        />
                      ),
                      value: 'burn-in-conditions',
                    } satisfies AccordionItem)
                  : null,
                {
                  picto: 'picto-security',
                  triggerText: t('articleDetail.details.safety'),
                  content: (
                    <>
                      <UL>
                        <ULI>
                          <Link rel="noopener noreferrer" target="_blank" to={t('articleDetail.details.safety.linkTo')}>
                            {t('articleDetail.details.safety.linkToText')}
                          </Link>
                        </ULI>
                      </UL>
                      {notNil(safetyLabel) ? (
                        <>
                          <Spacer size="05" />
                          <Image
                            alt={t('articleDetail.details.safety.labelText')}
                            baseSrc={safetyLabel}
                            className={styles.safetyImage}
                            imageSizes={ImageTransformType.safetyLabel}
                            type={ImageType.aspectRatio}
                          />
                          <Spacer size="05" />
                          <Link
                            download
                            icon="arrow-down-stroke-bottom"
                            rel="noopener noreferrer"
                            target="_blank"
                            to={safetyLabel}
                          >
                            {t('articleDetail.details.safety.labelDownloadText')}
                          </Link>
                        </>
                      ) : null}
                    </>
                  ),
                  value: 'safety',
                } satisfies AccordionItem,
                {
                  picto: 'picto-service',
                  triggerText: t('articleDetail.details.service'),
                  content: (
                    <UL>
                      <ULI>
                        <Link
                          rel="noopener noreferrer"
                          target="_blank"
                          to={t('articleDetail.details.service.brochure.linkTo')}
                        >
                          {t('articleDetail.details.service.brochure.linkToText')}
                        </Link>
                      </ULI>
                      <ULI>
                        <Link
                          rel="noopener noreferrer"
                          target="_blank"
                          to={t('articleDetail.details.service.about.linkTo')}
                        >
                          {t('articleDetail.details.service.about.linkToText')}
                        </Link>
                      </ULI>
                      <ULI>
                        <Link rel="noopener noreferrer" to={orderSampleSheet(articleId, shippingAddressId)}>
                          {t('articleDetail.details.service.orderSampleSheet.linkToText')}
                        </Link>
                      </ULI>
                      <ULI>
                        <Link rel="noopener noreferrer" to={serviceRequest(articleId)}>
                          {t('articleDetail.details.service.serviceRequest.linkToText')}
                        </Link>
                      </ULI>
                    </UL>
                  ),
                  value: 'service',
                } satisfies AccordionItem,
              ].filter(notNil)}
            />
          ) : null}
          {notNil(article.downloads) || article.certificateSections.length > 0 ? (
            <Downloads certificates={article.certificateSections} downloads={article.downloads} />
          ) : null}
        </GridItem>
      </GridContainer>
    </PageContainer>
  );
};
